import type { Payout } from '../types/payout';

export interface MonthGroup {
  year: number;
  month: number;
  monthName: string;
  payouts: Payout[];
  total: number;
  bncTotal: number;
}

export const groupPayoutsByMonth = (payouts: Payout[]): MonthGroup[] => {
  const groups = new Map<string, MonthGroup>();

  payouts.forEach(payout => {
    const date = new Date(payout.date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${month}`;

    if (!groups.has(key)) {
      const monthName = date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long'
      });

      groups.set(key, {
        year,
        month,
        monthName,
        payouts: [],
        total: 0,
        bncTotal: 0
      });
    }

    const group = groups.get(key)!;
    group.payouts.push(payout);
    if (payout.type === 'vente') {
      group.total -= payout.totalValue || 0;
    } else if (payout.type !== 'transfert' && payout.type !== 'swap') {
      group.total += payout.totalValue || 0;
    }
    if (payout.type === 'payout' || payout.type === 'stacking') {
      group.bncTotal += payout.totalValue || 0;
    }
  });

  return Array.from(groups.values())
    .sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
};

export const formatCurrency = (amount: number): string => {
  // Vérifier que amount est un nombre valide
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0,00 €';
  }
  
  // Déterminer le nombre de décimales selon la valeur
  let minimumFractionDigits = 2;
  let maximumFractionDigits = 2;
  
  if (amount < 0.01) {
    // Pour les très petites valeurs (< 0.01€), afficher jusqu'à 8 décimales
    minimumFractionDigits = 2;
    maximumFractionDigits = 8;
  } else if (amount < 1) {
    // Pour les valeurs < 1€, afficher jusqu'à 6 décimales
    minimumFractionDigits = 2;
    maximumFractionDigits = 6;
  } else if (amount < 100) {
    // Pour les valeurs < 100€, afficher jusqu'à 4 décimales
    minimumFractionDigits = 2;
    maximumFractionDigits = 4;
  }
  // Pour les valeurs >= 100€, garder 2 décimales par défaut
  
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits,
    maximumFractionDigits
  }).format(amount);
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Generate month key in format "YYYY-M" for comparing dates
 */
export const getMonthKey = (date: Date): string => {
  return `${date.getFullYear()}-${date.getMonth()}`;
};

/**
 * Generate month key from date string
 */
export const getMonthKeyFromString = (dateString: string): string => {
  return getMonthKey(new Date(dateString));
};

/**
 * Get current month key
 */
export const getCurrentMonthKey = (): string => {
  return getMonthKey(new Date());
};

/**
 * Get current year
 */
export const getCurrentYear = (): number => {
  return new Date().getFullYear();
};

/**
 * Get current month (0-11)
 */
export const getCurrentMonth = (): number => {
  return new Date().getMonth();
};

/**
 * Compare two month keys (returns -1, 0, or 1)
 */
export const compareMonthKeys = (key1: string, key2: string): number => {
  if (key1 < key2) return -1;
  if (key1 > key2) return 1;
  return 0;
};

/**
 * Get month name in French from date
 */
export const getMonthNameFR = (date: Date): string => {
  return date.toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Get display label for a month period
 */
export const getMonthLabel = (year: number, month: number): string => {
  const date = new Date(year, month);
  return getMonthNameFR(date);
};